import { EyeIcon } from "@heroicons/react/24/outline";
import { enumToItems } from "@utils/strings";
import { type Control, useWatch } from "react-hook-form";

import { Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

import { ModalityTypesEnum, PayorsEnum, ServicesOfferedEnum } from "@api/rest/types-and-hooks";

import { type ClinicFormValues } from "./AddEditClinicForm";

const serviceItems = enumToItems(ServicesOfferedEnum);
const modalityItems = enumToItems(ModalityTypesEnum);
const payorItems = enumToItems(PayorsEnum);

export const ClinicFormPreview: React.FC<{ control: Control<ClinicFormValues> }> = ({ control }) => {
  const name = useWatch({ control, name: "name" });
  const address = useWatch({ control, name: "address" });
  const about = useWatch({ control, name: "about" });
  const services = useWatch({ control, name: "services" }) ?? [];
  const modalities = useWatch({ control, name: "modalities" }) ?? [];
  const payors = useWatch({ control, name: "payors" }) ?? [];

  return (
    <div className="flex flex-col w-[25vw]">
      <div className="sticky top-20">
        <div className="flex flex-col gap-4 p-8 border border-border-medium bg-bg-surface rounded-xl">
          <div className="flex flex-row items-center gap-2">
            <EyeIcon className="size-4 text-text-secondary" />
            <Paragraph displayType="loud" colorType="primary">
              Preview
            </Paragraph>
          </div>

          <div className="flex flex-col">
            <Headline>{name}</Headline>
            <Paragraph size="small" colorType="secondary">
              {address}
            </Paragraph>
          </div>

          <Paragraph size="medium" colorType="primary">
            {about}
          </Paragraph>

          {services.length > 0 && (
            <div className="flex flex-col gap-1">
              <Paragraph displayType="loud" colorType="primary">
                Services
              </Paragraph>
              <div className="flex flex-row gap-2 flex-wrap">
                {services.map((service) => (
                  <div key={service} className="p-2 rounded-3xl bg-limestone-50">
                    <Paragraph colorType="secondary">{serviceItems.find((i) => i.id === service)?.title}</Paragraph>
                  </div>
                ))}
              </div>
            </div>
          )}

          {modalities.length > 0 && (
            <div className="flex flex-col gap-1">
              <Paragraph displayType="loud" colorType="primary">
                Modalities
              </Paragraph>
              <div className="flex flex-row gap-2 flex-wrap">
                {modalities.map((modality) => (
                  <div key={modality} className="p-2 rounded-3xl bg-limestone-50">
                    <Paragraph colorType="secondary">{modalityItems.find((i) => i.id === modality)?.title}</Paragraph>
                  </div>
                ))}
              </div>
            </div>
          )}

          {payors.length > 0 && (
            <div className="flex flex-col gap-1">
              <Paragraph displayType="loud" colorType="primary">
                Insurance accepted
              </Paragraph>
              <div className="flex flex-row gap-2 flex-wrap">
                {payors.map((payor) => (
                  <div key={payor} className="p-2 rounded-3xl bg-limestone-50">
                    <Paragraph colorType="secondary">{payorItems.find((i) => i.id === payor)?.title}</Paragraph>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
